/**
 * NotificationPrefs — server-side notification delivery preference panel.
 * Lets the owner choose, per kind, whether notifications surface at all:
 * the backend (job-queue-service) now PERSISTS this choice per (tenant,
 * user, kind) AND CONSULTS it before delivering a notification of that kind
 * on either channel (in-app row + best-effort email) — see
 * `collaboration_notification_handler.py` / `lead_assignment_handler.py`.
 * Default: everything ON — a kind is muted only once the owner explicitly
 * turns it off, so a fresh account sees the full, unfiltered feed (never a
 * silently empty bell) and delivery is unaffected until a choice is made.
 *
 * Backed by `GET`/`PUT /api/v1/notifications/preferences`
 * (`lib/api/notifications.ts`), which mirrors the same
 * `{ [kind]: boolean }` override-map shape this panel used to keep in
 * `localStorage` — a kind absent from the map is ON by default, so shipping
 * a brand-new kind surfaces (and delivers) it immediately without a
 * migration. Every network call is wrapped so a save/load failure degrades
 * gracefully (optimistic local state, never a crashed panel or a lost
 * toggle) rather than blocking the UI.
 *
 * `useNotificationKindPrefs` is also consumed by `./useNotifications.ts` to
 * filter the feed itself — this file has no dependency on that hook, so the
 * import only goes one way (no cycle). A module-level in-memory cache + a
 * same-tab pub-sub keep every mounted consumer (the bell's list AND this
 * panel, possibly open at once) in sync the instant a switch is toggled,
 * without refetching from the server on every read.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  getNotificationPreferences,
  KNOWN_NOTIFICATION_KINDS,
  NOTIFICATION_KIND_INFO,
  type NotificationGroup,
  putNotificationPreferences,
} from '@/lib/api/notifications';
import { Modal } from '@/ui';

/** kind -> surfaced/delivered. A key absent from the map means "on"
 *  (the default) — only explicit `false` overrides mute a kind. */
export type NotificationKindPrefs = Record<string, boolean>;

// Module-level cache shared by every mounted `useNotificationKindPrefs()`
// instance in this tab (the bell's list + the prefs panel), so only the
// FIRST mount triggers a `GET`; later toggles update the cache + the server
// optimistically without a refetch. There is no cross-tab sync (unlike the
// old `storage` event) — a change in another tab is picked up on that tab's
// own next load, which is an acceptable trade-off for a preferences panel.
let cachedPrefs: NotificationKindPrefs = {};
let loadState: 'idle' | 'loading' | 'loaded' = 'idle';
let loadPromise: Promise<void> | null = null;

const listeners = new Set<() => void>();
function emitChange(): void {
  listeners.forEach((fn) => fn());
}

/** Kicks off the one-time `GET` (idempotent — concurrent callers share the
 *  same in-flight promise). Never throws: `getNotificationPreferences()`
 *  already degrades 404/501/503 to `{}`; any other failure also degrades to
 *  "no overrides" here so the bell is never blocked on this load. */
function ensureLoaded(): Promise<void> {
  if (loadState === 'loaded') return Promise.resolve();
  if (loadPromise) return loadPromise;
  loadState = 'loading';
  loadPromise = getNotificationPreferences()
    .then((prefs) => {
      cachedPrefs = prefs;
    })
    .catch(() => {
      cachedPrefs = {};
    })
    .finally(() => {
      loadState = 'loaded';
      loadPromise = null;
      emitChange();
    });
  return loadPromise;
}

/** True unless the kind was explicitly turned off. */
export function isKindSurfaced(prefs: NotificationKindPrefs, kind: string | null | undefined): boolean {
  if (typeof kind !== 'string' || !kind) return true;
  return prefs[kind] !== false;
}

export interface UseNotificationKindPrefs {
  prefs: NotificationKindPrefs;
  isSurfaced: (kind: string | null | undefined) => boolean;
  setKindEnabled: (kind: string, on: boolean) => void;
  /** Clears every override — every kind goes back to the ON default. */
  resetAll: () => void;
}

export function useNotificationKindPrefs(): UseNotificationKindPrefs {
  const [prefs, setPrefs] = useState<NotificationKindPrefs>(cachedPrefs);

  useEffect(() => {
    const onChange = () => setPrefs({ ...cachedPrefs });
    listeners.add(onChange);
    void ensureLoaded().then(onChange);
    return () => {
      listeners.delete(onChange);
    };
  }, []);

  const persist = useCallback((next: NotificationKindPrefs) => {
    cachedPrefs = next;
    emitChange(); // instant UI feedback — the switch flips before the network resolves
    // Graceful on failure: the optimistic local/in-memory value is kept (the
    // toggle still applies for the rest of this session) even if the save
    // itself didn't reach the server; a future load re-syncs from there.
    void putNotificationPreferences(next).catch(() => {
      // best-effort — never surfaces to the UI, matches this client's
      // established degrade-gracefully posture (see lib/api/notifications.ts).
    });
  }, []);

  const setKindEnabled = useCallback(
    (kind: string, on: boolean) => {
      persist({ ...cachedPrefs, [kind]: on });
    },
    [persist],
  );

  const resetAll = useCallback(() => {
    persist({});
  }, [persist]);

  const isSurfaced = useCallback((kind: string | null | undefined) => isKindSurfaced(prefs, kind), [prefs]);

  return { prefs, isSurfaced, setKindEnabled, resetAll };
}

/* --------------------------------- panel --------------------------------- */

const GROUP_LABEL: Record<NotificationGroup, string> = {
  ai_staff: 'Équipe IA',
  collaboration: 'Collaboration',
};

function KindRow({
  kind,
  icon,
  label,
  enabled,
  onToggle,
}: {
  kind: string;
  icon: string;
  label: string;
  enabled: boolean;
  onToggle: (on: boolean) => void;
}) {
  return (
    <li className="flex items-center justify-between gap-3 py-2">
      <span className="flex min-w-0 items-center gap-2 text-sm text-foreground">
        <span aria-hidden="true" className="shrink-0 text-base leading-none">
          {icon}
        </span>
        <span className="truncate">{label}</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={`${label} — ${enabled ? 'activé' : 'désactivé'}`}
        onClick={() => onToggle(!enabled)}
        data-kind={kind}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          enabled ? 'bg-primary' : 'bg-border'
        }`}
      >
        <span
          aria-hidden="true"
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${
            enabled ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
    </li>
  );
}

export interface NotificationPrefsProps {
  onClose: () => void;
}

/**
 * The preferences panel itself — a Modal (per `./NotificationBell.tsx`'s
 * gear button) listing every known kind, grouped Équipe IA / Collaboration,
 * each with an on/off switch. Changes apply immediately (no "Enregistrer"
 * step — every toggle writes straight to localStorage) and a "Réinitialiser"
 * footer action clears all overrides back to the all-on default.
 */
export function NotificationPrefs({ onClose }: NotificationPrefsProps) {
  const { prefs, isSurfaced, setKindEnabled, resetAll } = useNotificationKindPrefs();

  const groups: NotificationGroup[] = ['ai_staff', 'collaboration'];

  return (
    <Modal
      title="Préférences de notifications"
      onClose={onClose}
      size="sm"
      footer={
        <button
          type="button"
          onClick={resetAll}
          disabled={Object.keys(prefs).length === 0}
          className="text-xs font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-40 disabled:no-underline"
        >
          Réinitialiser (tout afficher)
        </button>
      }
    >
      <div className="space-y-5">
        <p className="text-xs text-muted">
          Choisissez les types de notifications à recevoir. Ce réglage est enregistré sur votre
          compte : une fois désactivé, un type de notification n&rsquo;est plus envoyé, ni dans la
          cloche ni par courriel.
        </p>
        {groups.map((group) => {
          const kinds = KNOWN_NOTIFICATION_KINDS.filter(
            (kind) => NOTIFICATION_KIND_INFO[kind].group === group,
          );
          if (kinds.length === 0) return null;
          return (
            <div key={group}>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">
                {GROUP_LABEL[group]}
              </h3>
              <ul className="divide-y divide-border">
                {kinds.map((kind) => (
                  <KindRow
                    key={kind}
                    kind={kind}
                    icon={NOTIFICATION_KIND_INFO[kind].icon}
                    label={NOTIFICATION_KIND_INFO[kind].label}
                    enabled={isSurfaced(kind)}
                    onToggle={(on) => setKindEnabled(kind, on)}
                  />
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
